export const MENU_FULL_SCREEN_DEFAULT_SIZE = {
  width: 840,
  height: 560,
};

export const MENU_FULL_SCREEN_MIN_SIZE = {
  width: 640,
  height: 360,
};

export const MENU_FULL_SCREEN_VIEWPORT_PADDING = 80;

export const MENU_FULLSCREEN_HOST_SELECTOR = '[data-menu-fullscreen-host]';

export interface MenuFullScreenSize {
  width: number;
  height: number;
}

interface MenuFullScreenInlineStyleSnapshot {
  width: string;
  height: string;
  flex: string;
}

export interface MenuFullScreenPanelStyleSnapshot {
  panel: MenuFullScreenInlineStyleSnapshot;
  host: HTMLElement | null;
  hostStyle: MenuFullScreenInlineStyleSnapshot | null;
}

/** 记录缩放逻辑会覆盖的行内样式，退出全屏时按原值恢复。 */
function captureInlineStyle(element: HTMLElement): MenuFullScreenInlineStyleSnapshot {
  return {
    width: element.style.width,
    height: element.style.height,
    flex: element.style.flex,
  };
}

/** 在进入全屏前保存面板与外层 host 的普通态行内尺寸。 */
export function captureMenuFullScreenPanelStyle(
  panel: HTMLElement,
): MenuFullScreenPanelStyleSnapshot {
  const matchedHost = panel.closest(MENU_FULLSCREEN_HOST_SELECTOR);
  const host = matchedHost instanceof HTMLElement && matchedHost !== panel ? matchedHost : null;
  return {
    panel: captureInlineStyle(panel),
    host,
    hostStyle: host ? captureInlineStyle(host) : null,
  };
}

/** 退出全屏时恢复普通态尺寸，避免拖拽写入的宽高继续影响节点底部菜单。 */
export function restoreMenuFullScreenPanelStyle(
  panel: HTMLElement,
  snapshot: MenuFullScreenPanelStyleSnapshot,
) {
  const panelStyle = panel.style;
  panelStyle.width = snapshot.panel.width;
  panelStyle.height = snapshot.panel.height;
  panelStyle.flex = snapshot.panel.flex;

  const { host, hostStyle: savedHostStyle } = snapshot;
  if (host && savedHostStyle) {
    const hostStyle = host.style;
    hostStyle.width = savedHostStyle.width;
    hostStyle.height = savedHostStyle.height;
    hostStyle.flex = savedHostStyle.flex;
  }
}

/**
 * 读取面板当前实际尺寸，供拖拽起点使用（不做 clamp，避免按下时尺寸跳变）。
 */
export function readMenuFullScreenPanelSize(panel: HTMLElement): MenuFullScreenSize {
  return {
    width: panel.offsetWidth,
    height: panel.offsetHeight,
  };
}

export interface MenuFullScreenResizeSession {
  anchorX: number;
  anchorY: number;
  pointerOffsetX: number;
  pointerOffsetY: number;
}

/**
 * 记录全屏面板右下角拖拽起点。面板以视口中心为锚点对称缩放，尺寸需按 2 倍指针位移计算。
 */
export function createMenuFullScreenResizeSession(
  panel: HTMLElement,
  clientX: number,
  clientY: number,
): MenuFullScreenResizeSession {
  const rect = panel.getBoundingClientRect();
  return {
    anchorX: rect.left + rect.width / 2,
    anchorY: rect.top + rect.height / 2,
    pointerOffsetX: clientX - rect.right,
    pointerOffsetY: clientY - rect.bottom,
  };
}

/**
 * 根据指针位置计算下一档全屏面板尺寸，使右下角缩放柄跟随鼠标。
 */
export function resolveMenuFullScreenResizeSize(
  session: MenuFullScreenResizeSession,
  clientX: number,
  clientY: number,
): MenuFullScreenSize {
  const targetRight = clientX - session.pointerOffsetX;
  const targetBottom = clientY - session.pointerOffsetY;
  return clampMenuFullScreenSize({
    width: 2 * (targetRight - session.anchorX),
    height: 2 * (targetBottom - session.anchorY),
  });
}

/**
 * 将全屏面板尺寸同步到容器及其外层 host，保证四角一起随中心缩放。
 */
export function applyMenuFullScreenPanelSize(
  panel: HTMLElement,
  nextSize: MenuFullScreenSize,
) {
  const panelStyle = panel.style;
  panelStyle.width = `${nextSize.width}px`;
  panelStyle.height = `${nextSize.height}px`;

  const host = panel.closest(MENU_FULLSCREEN_HOST_SELECTOR);
  if (host instanceof HTMLElement && host !== panel) {
    host.style.width = `${nextSize.width}px`;
    host.style.height = `${nextSize.height}px`;
    host.style.flex = `0 0 ${nextSize.width}px`;
  }
}

/**
 * 根据当前视口计算提示词全屏面板允许的最大宽高。
 */
export function getMenuFullScreenMaxSize(): MenuFullScreenSize {
  if (typeof window === 'undefined') {
    return MENU_FULL_SCREEN_DEFAULT_SIZE;
  }

  return {
    width: Math.max(
      MENU_FULL_SCREEN_MIN_SIZE.width,
      window.innerWidth - MENU_FULL_SCREEN_VIEWPORT_PADDING,
    ),
    height: Math.max(
      MENU_FULL_SCREEN_MIN_SIZE.height,
      window.innerHeight - MENU_FULL_SCREEN_VIEWPORT_PADDING,
    ),
  };
}

/**
 * 将提示词全屏面板尺寸限制在设计允许的最小值与当前视口上限之间。
 */
export function clampMenuFullScreenSize(size: MenuFullScreenSize): MenuFullScreenSize {
  const maxSize = getMenuFullScreenMaxSize();
  return {
    width: Math.min(
      maxSize.width,
      Math.max(MENU_FULL_SCREEN_MIN_SIZE.width, Math.round(size.width)),
    ),
    height: Math.min(
      maxSize.height,
      Math.max(MENU_FULL_SCREEN_MIN_SIZE.height, Math.round(size.height)),
    ),
  };
}
